import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, NavLink, useLocation, Link } from "react-router-dom";
import { Toaster } from "sonner";
import { LayoutDashboard, TrendingUp, Sparkles, Library, Settings as SettingsIcon, Flame, CalendarDays, Mail, CreditCard, Coins, LogOut, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Trends from "@/pages/Trends";
import Studio from "@/pages/Studio";
import LibraryPage from "@/pages/Library";
import CalendarPage from "@/pages/Calendar";
import DigestPage from "@/pages/Digest";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import SettingsPage from "@/pages/Settings";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/trends", label: "Trend Radar", icon: TrendingUp, testid: "nav-trends" },
  { to: "/studio", label: "Content Studio", icon: Sparkles, testid: "nav-studio" },
  { to: "/library", label: "Library", icon: Library, testid: "nav-library" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/digest", label: "Daily Digest", icon: Mail, testid: "nav-digest" },
  { to: "/pricing", label: "Upgrade", icon: CreditCard, testid: "nav-pricing" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

function CreditsBadge() {
  const [b, setB] = useState(null);
  const loc = useLocation();
  useEffect(() => { api.get("/billing").then((r) => setB(r.data)).catch(() => {}); }, [loc.pathname]);
  if (!b) return null;
  return (
    <Link to="/pricing" data-testid="sidebar-credits" className="flex items-center justify-between gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-md px-3 py-2.5 transition-colors group">
      <div className="flex items-center gap-2">
        <Coins className="w-4 h-4 text-signal-yellow" />
        <span className="font-mono text-sm">{b.pro_active ? "Pro · ∞" : `${b.credits} credits`}</span>
      </div>
      <span className="text-[10px] uppercase tracking-wider text-brand font-mono opacity-0 group-hover:opacity-100 transition-opacity">{b.pro_active ? "" : "top up"}</span>
    </Link>
  );
}

function UserCard() {
  const { user, logout } = useAuth();
  if (!user) return null;
  return (
    <div className="flex items-center gap-2.5 mt-3" data-testid="sidebar-user">
      {user.picture ? (
        <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-white/10" referrerPolicy="no-referrer" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-brand/20 border border-brand/30 flex items-center justify-center text-xs font-mono">{(user.name || user.email || "?")[0].toUpperCase()}</div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm truncate leading-tight">{user.name || "Creator"}</div>
        <div className="text-[10px] text-white/40 font-mono truncate">{user.email}</div>
      </div>
      <button onClick={logout} data-testid="logout-btn" title="Sign out"
        className="text-white/40 hover:text-brand transition-colors p-1.5 rounded hover:bg-white/5">
        <LogOut className="w-4 h-4" />
      </button>
    </div>
  );
}

function Sidebar() {
  return (
    <aside className="hidden md:flex flex-col w-64 shrink-0 bg-[#0A0A0A] border-r border-white/10 h-screen sticky top-0">
      <div className="px-6 py-6 border-b border-white/10 flex items-center gap-2">
        <div className="w-9 h-9 rounded-md bg-brand flex items-center justify-center">
          <Flame className="w-5 h-5 text-white" strokeWidth={2} />
        </div>
        <div>
          <div className="font-heading text-lg font-semibold leading-none tracking-tight">TrendForge</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono mt-1">Creator OS</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            data-testid={item.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                isActive ? "bg-white/10 text-white" : "text-white/50 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <item.icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-5 py-4 border-t border-white/10">
        <CreditsBadge />
        <UserCard />
      </div>
    </aside>
  );
}

function MobileNav() {
  const loc = useLocation();
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/10 flex justify-around py-2">
      {navItems.map((item) => {
        const active = item.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(item.to);
        return (
          <NavLink key={item.to} to={item.to} data-testid={`${item.testid}-mobile`}
            className={`flex flex-col items-center gap-1 px-2 py-1 ${active ? "text-brand" : "text-white/40"}`}>
            <item.icon className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[9px] font-mono">{item.label.split(" ")[0]}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function Shell({ children }) {
  return (
    <div className="App grain flex min-h-screen bg-[#0A0A0A] relative">
      <Sidebar />
      <main className="flex-1 min-w-0 pb-20 md:pb-0 relative z-10">{children}</main>
      <MobileNav />
    </div>
  );
}

function AppShellRoutes() {
  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/trends" element={<Trends />} />
        <Route path="/studio" element={<Studio />} />
        <Route path="/library" element={<LibraryPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/digest" element={<DigestPage />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Routes>
    </Shell>
  );
}

function Gate() {
  const location = useLocation();
  const { user, loading } = useAuth();

  // Process the OAuth return FIRST (session_id lives in the URL fragment).
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center" data-testid="auth-loading">
        <Loader2 className="w-6 h-6 text-brand animate-spin" />
      </div>
    );
  }
  if (!user) return <Landing />;
  return <AppShellRoutes />;
}

function App() {
  return (
    <BrowserRouter>
      <Toaster theme="dark" position="top-right" richColors />
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
